import { Link } from 'react-router-dom';

export interface ArticleSummary {
  id: string;
  title: string;
  summary: string;
  category: string;
  region: string;
  date: string;
  readMinutes: number;
  cover: string;
  tags: string[];
}

interface ArticleCardProps {
  article: ArticleSummary;
  variant?: 'default' | 'list';
}

function CategoryTag({ category }: { category: string }) {
  return (
    <span className="inline-flex items-center rounded-sm bg-primary-50 px-2 py-0.5 text-[11px] font-medium text-primary-700">
      {category}
    </span>
  );
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'list') {
    return (
      <article className="group flex flex-col gap-4 border-b border-background-200 pb-6 sm:flex-row sm:gap-6">
        <Link
          to={`/cases/${article.id}`}
          className="block h-40 w-full shrink-0 overflow-hidden rounded-lg bg-background-200 sm:h-32 sm:w-52"
        >
          <img
            src={article.cover}
            alt={`${article.title} 配图`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex flex-wrap items-center gap-2">
            <CategoryTag category={article.category} />
            <span className="text-[11px] text-foreground-500">{article.region}</span>
          </div>
          <h3 className="mt-2 font-heading text-base font-semibold leading-snug text-foreground-950 md:text-lg">
            <Link to={`/cases/${article.id}`} className="transition-colors hover:text-primary-700">
              {article.title}
            </Link>
          </h3>
          <p className="mt-2 hidden text-sm leading-relaxed text-foreground-600 sm:line-clamp-2">{article.summary}</p>
          <div className="mt-auto flex items-center gap-4 pt-3 text-[11px] text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {article.date}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.readMinutes} 分钟
            </span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-200">
      <Link to={`/cases/${article.id}`} className="block h-48 w-full overflow-hidden bg-background-200">
        <img
          src={article.cover}
          alt={`${article.title} 配图`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </Link>
      <div className="flex flex-1 flex-col p-5">
        <div className="flex flex-wrap items-center gap-2">
          <CategoryTag category={article.category} />
          <span className="text-[11px] text-foreground-500">{article.date}</span>
        </div>
        <h3 className="mt-3 font-heading text-lg font-semibold leading-snug text-foreground-950">
          <Link to={`/cases/${article.id}`} className="transition-colors group-hover:text-primary-700">
            {article.title}
          </Link>
        </h3>
        <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-foreground-600">{article.summary}</p>
        <div className="mt-auto flex items-center justify-between pt-5 text-[11px] text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-map-pin-line"></i>
            {article.region}
          </span>
          <span className="flex items-center gap-1 font-medium text-primary-600">
            阅读全文
            <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5"></i>
          </span>
        </div>
      </div>
    </article>
  );
}